import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import TrianglesMiniSvg from '../other/svg/TrianglesMiniSvg'
import { fieldBoxShadow } from './FormTextInput'

interface FormMonthPickerProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  error?: boolean
  yearsDirection?: 'past' | 'future'
}

interface ParsedMonth {
  year: number
  monthIndex: number
}

interface MenuPosition {
  left: number
  top: number | null
  bottom: number | null
}

const PANEL_WIDTH = 320
const MIN_SPACE_BELOW = 220
const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const FALLBACK_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const parseMonth = (value: string): ParsedMonth | null => {
  if (value.length < 7 || value.charAt(4) !== '-') {
    return null
  }
  const year = Number(value.slice(0, 4))
  const month = Number(value.slice(5, 7))
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null
  }
  return { year, monthIndex: month - 1 }
}

const padMonth = (monthIndex: number): string => String(monthIndex + 1).padStart(2, '0')

const FormMonthPicker = ({
  value,
  onChange,
  placeholder,
  error = false,
  yearsDirection = 'past'
}: FormMonthPickerProps): JSX.Element => {
  const { t } = useTranslation()
  const controlRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [stage, setStage] = useState<'year' | 'month'>('year')
  const [year, setYear] = useState(() => parseMonth(value)?.year ?? new Date().getFullYear())
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)

  const months = useMemo((): string[] => {
    const translated: unknown = t('my_vehicles.promotion_months', { returnObjects: true })
    if (!Array.isArray(translated)) {
      return FALLBACK_MONTHS
    }
    const names = translated.filter((item): item is string => typeof item === 'string')
    return names.length === 12 ? names : FALLBACK_MONTHS
  }, [t])

  const parsed = parseMonth(value)
  const isPlaceholder = parsed === null
  const displayValue = parsed === null ? placeholder : `${months[parsed.monthIndex]} ${parsed.year}`

  const years =
    yearsDirection === 'future'
      ? Array.from({ length: 12 }, (_, index) => year - 2 + index)
      : Array.from({ length: 12 }, (_, index) => year - (11 - index))

  useLayoutEffect(() => {
    const updatePosition = (): void => {
      if (controlRef.current === null) {
        return
      }
      const rect = controlRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const openAbove = spaceBelow < MIN_SPACE_BELOW && spaceAbove > spaceBelow
      const maxLeft = window.innerWidth - PANEL_WIDTH - 8
      const left = Math.max(8, Math.min(rect.left, maxLeft))
      setMenuPosition({
        left,
        top: openAbove ? null : rect.bottom + 4,
        bottom: openAbove ? window.innerHeight - rect.top + 4 : null
      })
    }

    if (isOpen) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
    } else {
      setMenuPosition(null)
    }

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const openPicker = (): void => {
    setStage('year')
    if (parsed !== null) {
      setYear(parsed.year)
    }
    setIsOpen(true)
  }

  const shiftYear = (offset: number): void => {
    setYear((current) => current + offset)
  }

  const cellClassName = (selected: boolean): string =>
    [
      'whitespace-nowrap rounded-md px-2 py-1 font-Montserrat text-xs font-semibold',
      selected ? 'bg-Estigroup-primary text-white' : 'text-foregrounds-fg-base hover:bg-backgrounds-bg-component-hover'
    ].join(' ')

  return (
    <div className="self-stretch relative">
      <div
        ref={controlRef}
        className="self-stretch w-full h-11 pl-4 pr-1 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center overflow-hidden"
        style={{ boxShadow: fieldBoxShadow(error) }}>
        <button
          type="button"
          onClick={openPicker}
          className={`flex-1 h-full text-left text-sm font-Montserrat leading-5 truncate bg-transparent ${
            isPlaceholder ? 'text-foregrounds-fg-muted font-medium' : 'text-foregrounds-fg-base font-semibold'
          }`}>
          {displayValue}
        </button>
        <button type="button" onClick={openPicker} className="size-8 flex justify-center items-center shrink-0">
          <TrianglesMiniSvg className="size-[15px] text-foregrounds-fg-muted" />
        </button>
      </div>
      {isOpen &&
        menuPosition !== null &&
        createPortal(
          <>
            <div
              role="presentation"
              className="fixed inset-0 z-[100]"
              onClick={() => {
                setIsOpen(false)
              }}
            />
            <div
              className="fixed z-[200] w-[320px] rounded-lg bg-backgrounds-bg-field-component p-4"
              style={{
                left: menuPosition.left,
                top: menuPosition.top ?? 'auto',
                bottom: menuPosition.bottom ?? 'auto',
                boxShadow: menuShadow
              }}>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={() => {
                    shiftYear(stage === 'year' ? -12 : -1)
                  }}
                  className="flex size-8 items-center justify-center rounded-full bg-Estigroup-primary text-white">
                  <span className="box-border block size-2.5 translate-x-px -rotate-[135deg] border-t-2 border-r-2 border-solid border-white" />
                </button>
                <div className="font-Montserrat text-base font-medium text-foregrounds-fg-base">
                  {stage === 'year' ? `${years[0]} – ${years[11]}` : year}
                </div>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={() => {
                    shiftYear(stage === 'year' ? 12 : 1)
                  }}
                  className="flex size-8 items-center justify-center rounded-full bg-Estigroup-primary text-white">
                  <span className="box-border block size-2.5 -translate-x-px rotate-45 border-t-2 border-r-2 border-solid border-white" />
                </button>
              </div>
              {stage === 'year' ? (
                <div className="mt-4 grid grid-cols-3 gap-y-3 text-center">
                  {years.map((mapYear) => (
                    <div key={mapYear} className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          setYear(mapYear)
                          setStage('month')
                        }}
                        className={cellClassName(parsed !== null && parsed.year === mapYear)}>
                        {mapYear}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-3 gap-y-3 text-center">
                  {months.map((month, monthIndex) => (
                    <div key={month} className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          onChange(`${year}-${padMonth(monthIndex)}`)
                          setIsOpen(false)
                        }}
                        className={cellClassName(
                          parsed !== null && parsed.year === year && parsed.monthIndex === monthIndex
                        )}>
                        {month}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

export default FormMonthPicker
